import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import pokemonCards from '../data/pokemon_cards.json';
import pokeBallImg from '../assets/images/poke_ball.webp';
import greatBallImg from '../assets/images/great_ball.webp';
import ultraBallImg from '../assets/images/ultra_ball.webp';
import masterBallImg from '../assets/images/master_ball.webp';
import DeckCard from '../components/DeckCard';
import { useSelector, useDispatch } from 'react-redux';
import api from '../api/config';
import toast from 'react-hot-toast';
import { loginSuccess } from '../redux/features/authSlice';
import { useUpdateProfile, useUserProfile } from '../api/auth';
import { Link } from 'react-router-dom';
import { useOwnedCards } from '../api/cards';
import { motion } from 'framer-motion';
import {useCollectionStats, useGetUserDecks} from '@/api/profile';

// Rank image mapping
const rankImages = {
  'Beginner': pokeBallImg,
  'Poké Ball': pokeBallImg,
  'Great Ball': greatBallImg,
  'Ultra Ball': ultraBallImg,
  'Master Ball': masterBallImg,
};

// Image component copied from Binder.js for consistent binder image rendering
const Image = ({ src, alt, className, onLoad }) => {
  const [isLoaded, setIsLoaded] = React.useState(false);

  const handleImageLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  return (
    <div className="relative">
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-800 animate-pulse rounded-lg">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${className} ${!isLoaded ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
        onLoad={handleImageLoad}
      />
    </div>
  );
};

const rankDisplay = (rank) => {
  if (rank.tier === 'Master Ball') return 'Master Ball Rank';
  return `${rank.tier} Rank ${rank.level}`;
};

function EditProfileModal({ open, onClose, user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutate: updateProfile, isLoading } = useUpdateProfile();
  const [form, setForm] = React.useState({
    username: user.username,
    friendId: user.friend_id || '',
    rankTier: user.rank?.tier || 'Beginner',
    rankLevel: user.rank?.level,
  });

  React.useEffect(() => {
    if (open) {
      // Parse rank from profile data
      const rankParts = user.rank?.split(' - ') || ['Beginner'];
      const rankTier = rankParts[0];
      const rankLevel = rankParts[1] ? parseInt(rankParts[1]) : undefined;

      setForm({
        username: user.username,
        friendId: user.friend_id || '',
        rankTier: rankTier,
        rankLevel: rankLevel,
      });
    }
  }, [open, user]);
  

  const formatFriendId = (value) => {
    const digits = value.replace(/\D/g, '');
    let result = '';
    for (let i = 0; i < digits.length && i < 16; i += 4) {
      if (i > 0) result += '-';
      result += digits.substr(i, 4);
    }
    return result;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'friendId' ? formatFriendId(value) : value
    }));
  };

  const handleSave = () => {
    if (!form.username.trim()) {
      toast.error('Username is required!');
      return;
    }
    const payload = {
      username: form.username,
      friend_id: form.friendId,
      rank: form.rankTier + (form.rankTier !== 'Master Ball' && form.rankTier !== 'Beginner' ? ` - ${form.rankLevel || 1}` : ''),
    };
    updateProfile(payload, {
      onSuccess: (response) => {
        // Check if response exists
        if (response) {
          // Update Redux state
          dispatch(loginSuccess(response));
          
          // Update localStorage
          const auth = JSON.parse(localStorage.getItem('auth')) || {};
          auth.user = response;
          localStorage.setItem('auth', JSON.stringify(auth));
          
          // Show success message
          toast.success('Profile updated successfully!');
          
          // Close the modal
          onClose();
          
          // If username was changed, handle navigation and state updates
          if (form.username !== user.username) {
            // Update the URL with new username
            navigate(`/profile/${form.username}`, { replace: true });
            
            // Force a refetch of the profile data
            window.location.reload();
          }
        } else {
          console.error('Empty response received');
          toast.error('Failed to update profile: Empty response from server');
        }
      },
      onError: (err) => {
        console.error('Profile update error:', err);
        const errorMessage = err.response?.data?.message || 'Failed to update profile';
        toast.error(errorMessage);
      }
    });
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-xl shadow-xl border border-purple-500/30 w-full max-w-md p-8 relative">
        {isLoading && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-50 rounded-xl">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500 mb-4"></div>
            <span className="text-white text-lg font-semibold">Saving...</span>
          </div>
        )}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl"
          onClick={onClose}
          aria-label="Close"
          disabled={isLoading}
        >
          ×
        </button>
        <h2 className="text-2xl font-bold mb-6 text-white">Edit Profile</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-400 mb-1">Username</label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              className={`w-full px-4 py-2 rounded bg-gray-800 border ${!form.username.trim() ? 'border-red-500' : 'border-purple-500/20'} text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              disabled={isLoading}
            />
            {!form.username.trim() && (
              <span className="text-red-400 text-sm mt-1 block">Username is required.</span>
            )}
          </div>
          <div>
            <label className="block text-gray-400 mb-1">Friend ID</label>
            <input
              type="text"
              name="friendId"
              value={form.friendId}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded bg-gray-800 border border-purple-500/20 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-gray-400 mb-1">Rank</label>
            <select
              name="rankTier"
              value={form.rankTier}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded bg-gray-800 border border-purple-500/20 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 mb-2"
              disabled={isLoading}
            >
              <option value="Beginner">Beginner</option>
              <option value="Poké Ball">Poké Ball</option>
              <option value="Great Ball">Great Ball</option>
              <option value="Ultra Ball">Ultra Ball</option>
              <option value="Master Ball">Master Ball</option>
            </select>
            {form.rankTier !== 'Master Ball' && form.rankTier !== 'Beginner' && (
              <select
                name="rankLevel"
                value={form.rankLevel}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded bg-gray-800 border border-purple-500/20 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isLoading}
              >
                {[1, 2, 3, 4].map((lvl) => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-4 mt-8">
          <button
            className="px-4 py-2 rounded bg-gray-700 text-gray-300 hover:bg-gray-600"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="px-6 py-2 rounded bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium hover:from-purple-500 hover:to-blue-500 focus:ring-2 focus:ring-purple-500 focus:outline-none"
            onClick={handleSave}
            disabled={isLoading || !form.username.trim()}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const heroVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const transformTemplate = ({ x, y, scale }) => {
  return `translate3d(${x || 0}px, ${y || 0}px, 0) scale(${scale || 1})`;
};

const Profile = () => {
  const {data: collectionStats} = useCollectionStats();
  const {data: userDecks} = useGetUserDecks();
  const { username } = useParams();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const [editOpen, setEditOpen] = React.useState(false);
  const { data: profile, isLoading, isError, error } = useUserProfile(username);
  const { data: ownedCardsData } = useOwnedCards(username);
  const ownedCards = ownedCardsData?.owned_cards || {};
  const rankImg = profile && profile.rank ? (rankImages[profile.rank.split(' - ')[0]] || rankImages['Poké Ball']) : rankImages['Poké Ball'];
  // Flatten cards from all sets and take the first 12 for the binder demo
  const binderCards = React.useMemo(() => {
    if (!profile || !profile.owned_cards) return [];
    // Assuming owned_cards is an array of card objects or IDs
    return profile.owned_cards.slice(0, 12);
  }, [profile]);

  // Tabs state
  const [activeTab, setActiveTab] = React.useState('binder');

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div></div>;
  }
  if (isError || !profile) {
    if (error?.response?.status === 404) {
      return (
        <div className="min-h-screen flex items-center justify-center relative bg-gradient-to-br animate-gradient-background bg-[length:400%_400%]">
          <div className="absolute inset-0 opacity-50" style={{
            backgroundImage: `radial-gradient(circle at center, transparent 0%, rgba(0, 0, 0, 0.8) 100%)`
          }}></div>
          <div className="relative max-w-2xl w-full mx-4 p-8">
            {/* Background Effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black rounded-2xl"></div>
            <div 
              className="absolute inset-0 opacity-20 rounded-2xl"
              style={{
                backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)`,
                backgroundSize: '20px 20px'
              }}
            ></div>
            <div className="absolute inset-0.5 rounded-2xl bg-black/50 backdrop-blur-md border border-white/10"></div>
            <div className="relative text-center space-y-8">
              <h1 className="text-8xl font-pokemon tracking-wider mb-2">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500">
                  404
                </span>
              </h1>
              <h2 className="text-2xl font-pokemon text-white">
                Trainer Not Found!
              </h2>
              <p className="text-gray-400 max-w-md mx-auto">
                Looks like this trainer hasn't been discovered yet.<br />
                Maybe try searching for another username?
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/"
                  className="relative inline-flex items-center px-6 py-3 rounded-lg overflow-hidden group w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-90 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute inset-0.5 rounded-lg bg-black/90 group-hover:bg-black/80 transition-colors duration-300"></div>
                  <span className="relative text-white font-medium">Return Home</span>
                </Link>
                <Link
                  to="/decks"
                  className="relative inline-flex items-center px-6 py-3 rounded-lg overflow-hidden group w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 opacity-90 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute inset-0.5 rounded-lg bg-black/90 group-hover:bg-black/80 transition-colors duration-300"></div>
                  <span className="relative text-white font-medium">Browse Decks</span>
                </Link>
              </div>
              {/* Decorative Elements */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div className="absolute top-4 left-4 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="absolute top-4 right-4 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <div className="absolute bottom-4 left-4 w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <div className="absolute bottom-4 right-4 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <div className="flex justify-center items-center min-h-screen text-red-500">{error?.response?.data?.message || 'Failed to load profile.'}</div>;
  }

  return (
    <div>
      {/* Hero Section */}
      <motion.div 
        className="relative overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)`,
              backgroundSize: '20px 20px'
            }}
          ></div>
        </div>
        
        <motion.div 
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 mt-24"
        >
          <div className="text-center">
            <motion.div 
              className="flex flex-col items-center justify-center mb-8"
              variants={itemVariants}
            >
              <h1 className="mt-4 text-4xl font-pokemon tracking-wider">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-green-500 to-blue-500">
                  {profile.username}
                </span>
              </h1>
              {profile?.rank?.tier && <div className="mt-2 flex items-center gap-2">
                <img
                  src={rankImg}
                  alt={profile.rank.tier + " Ball"}
                  className="w-8 h-8"
                />
                <span className="text-lg font-semibold text-purple-200">
                  {rankDisplay(profile.rank)}
                </span>
              </div>}
            </motion.div>
            <motion.div 
              className="grid grid-cols-2 gap-4 max-w-lg mx-auto"
              variants={itemVariants}
            >
              <div className="bg-gray-900/50 rounded-lg p-4 border border-purple-500/20">
                <div className="text-2xl font-bold text-purple-400">{collectionStats?.user_deck_count || 0}</div>
                <div className="text-sm text-gray-400">Decks</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-4 border border-purple-500/20">
                <div className="text-2xl font-bold text-purple-400">{collectionStats?.owned_card_count || 0}</div>
                <div className="text-sm text-gray-400">Owned Cards</div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      <div className="max-w-5xl mx-auto px-4 mt-12 pb-12">
        <section className="mb-8 bg-gray-900/70 rounded-xl p-6 border border-purple-500/20 shadow">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            {/* Game Info Left */}
            <div className="flex-1">
              <h2 className="text-2xl font-semibold mb-2 text-white">Trainer Information</h2>
              <ul className="text-gray-300 text-lg space-y-1">
                <li><strong>Username:</strong> {profile.username}</li>
                {profile.friend_id?.length > 0 && <li><strong>Friend ID:</strong> <span className="font-mono bg-gray-800 px-2 py-1 rounded">{profile.friend_id}</span></li>}
              </ul>
              {loggedInUser && (loggedInUser.username === username || loggedInUser.id?.toString() === username) && (
                <button
                  className="mt-4 px-4 py-2 rounded bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium hover:from-purple-500 hover:to-blue-500 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  onClick={() => setEditOpen(true)}
                >
                  Edit Profile
                </button>
              )}
            </div>
            {/* Rank Right */}
            {profile?.rank && <div className="flex flex-col items-center md:w-64 w-full mt-8 md:mt-0">
              <img
                src={rankImg}
                alt={profile.rank}
                className="w-16 h-16 md:w-20 md:h-20 mb-2 drop-shadow-lg"
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              />
              <div className="text-xl font-bold text-white mb-1">{profile.rank}</div>
              
            </div>}
          </div>
        </section>

        {/* Tabs */}
        <div className="mb-6 flex border-b border-purple-500/20">
          <button
            className={`px-6 py-3 text-lg font-medium focus:outline-none transition-colors duration-200 ${activeTab === 'binder' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-400 hover:text-white'}`}
            onClick={() => setActiveTab('binder')}
          >
            Binder
          </button>
          <button
            className={`px-6 py-3 text-lg font-medium focus:outline-none transition-colors duration-200 ${activeTab === 'decks' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-400 hover:text-white'}`}
            onClick={() => setActiveTab('decks')}
          >
            Decks
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'binder' && (
          <section className="bg-gray-900/70 rounded-xl p-6 border border-purple-500/20 shadow relative overflow-hidden">
            <h2 className="text-2xl font-semibold mb-4 text-white">Binder</h2>
            {Object.keys(ownedCards).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 relative z-10">
                <img src={pokeBallImg} alt="Poké Ball" className="w-24 h-24 mb-4 opacity-60 drop-shadow-lg" />
                <h3 className="text-2xl font-pokemon text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500 mb-2">No Pokémon cards found!</h3>
                <p className="text-gray-400 text-center max-w-xs mb-2">Go catch some cards to fill your binder and become a true Pokémon Master!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {Object.entries(ownedCards).map(([cardId, cardInfo]) => (
                  <motion.div 
                    key={cardId} 
                    className="rounded-lg overflow-hidden bg-gray-900 border border-purple-500/20 shadow group relative hover:border-pink-500 transition-all duration-300 transform hover:scale-105"
                    transformTemplate={transformTemplate}
                  >
                    <div className="aspect-w-1 aspect-h-1">
                      <img
                        src={cardInfo.image_url}
                        alt={cardId}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                      <h3 className="text-white text-sm font-semibold group-hover:text-pink-500 transition-colors duration-300">
                        {cardInfo.name}
                      </h3>
                      <span className="text-xs text-gray-300">Quantity: {cardInfo.quantity}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
            <img src={pokeBallImg} alt="Poké Ball" className="absolute -bottom-8 -right-8 w-40 h-40 opacity-10 pointer-events-none select-none" />
          </section>
        )}
        {activeTab === 'decks' && (
          <section className="bg-gray-900/70 rounded-xl p-6 border border-purple-500/20 shadow relative overflow-hidden">
            <h2 className="text-2xl font-semibold mb-4 text-white">Decks</h2>
            {userDecks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 relative z-10">
                <img src={pokeBallImg} alt="Poké Ball" className="w-24 h-24 mb-4 opacity-60 drop-shadow-lg" />
                <h3 className="text-2xl font-pokemon text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-blue-400 to-purple-500 mb-2">No decks created yet!</h3>
                <p className="text-gray-400 text-center max-w-xs mb-2">Build your first deck and start your Pokémon adventure!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {userDecks.map(deck => (
                  <DeckCard key={deck.id} deck={deck} profile={true} />
                ))}
              </div>
            )}
            {/* Decorative Poké Ball BG */}
            <img src={pokeBallImg} alt="Poké Ball" className="absolute -bottom-8 -right-8 w-40 h-40 opacity-10 pointer-events-none select-none" />
          </section>
        )}
      </div>
      <EditProfileModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        user={profile}
      />
    </div>
  );
};

export default Profile; 